import { mapInfo, teleportCommand } from "@69ly/maps";
import { importanceRank, type Stuff } from "@69ly/shared";
import { useTranslation } from "react-i18next";
import { MediaViewer } from "../components/StuffMedia.tsx";
import { Link, Navigate } from "react-router";
import { CopyButton } from "../components/CopyButton.tsx";
import { GrenadeGlyph } from "../components/GrenadeGlyph.tsx";
import { StatusControl } from "../components/StatusControl.tsx";
import { ErrorState, Loading } from "../components/Status.tsx";
import { WindowsBar } from "../components/WindowsBar.tsx";
import { useCurrentTeam } from "../lib/current-team.tsx";
import { errorText } from "../lib/errors.ts";
import { useStuffs } from "../lib/stuff-queries.ts";
import { MapBar, useMapParam } from "./MapView.tsx";
import { StuffLink } from "../components/StuffDrawer.tsx";

/** Generic stuffs first (thrown every round), then useful, then situational; inside each, practising before to learn, newest first. */
export function learnQueue(stuffs: Stuff[]): Stuff[] {
  const rank = (s: Stuff) => (s.myStatus === "practising" ? 0 : 1);
  return stuffs
    .filter((s) => s.myStatus === "to_learn" || s.myStatus === "practising")
    .sort((a, b) => importanceRank(a.importance) - importanceRank(b.importance) || rank(a) - rank(b) || b.updatedAt.localeCompare(a.updatedAt));
}

function LearnCard({ stuff, index }: { stuff: Stuff; index: number }) {
  const { t } = useTranslation();
  const tech = stuff.technique;
  const command = teleportCommand(stuff.throw);
  return (
    <li className="card learn-card">
      <div className="learn-card-head">
        <span className="learn-index" aria-hidden="true">
          {index + 1}
        </span>
        <GrenadeGlyph grenade={stuff.grenade} size={18} />
        <div className="learn-card-title">
          <h2 className="panel-title">
            <StuffLink id={stuff.id}>{stuff.title}</StuffLink>
          </h2>
          <p className="muted small">
            {t(`side.${stuff.side}`)} · {t(`area.${stuff.area}`)} · {t(`movement.${tech.movement}`)}
            {tech.jump ? `, ${t("form.jump").toLowerCase()}` : ""} · {t(`click.${tech.click}`)}
          </p>
        </div>
      </div>
      <MediaViewer stuff={stuff} empty={t("detail.noPictures")} />
      <div className="command">
        <code>{command}</code>
        <CopyButton text={command} />
      </div>
      {stuff.windows.length > 0 && <WindowsBar windows={stuff.windows} />}
      <div className="learn-card-foot">
        <StatusControl stuffId={stuff.id} status={stuff.myStatus} />
      </div>
    </li>
  );
}

/** Learn mode: the stuffs I track on this map and have not learned yet, one after the other. */
export function LearnPage() {
  const { t } = useTranslation();
  const map = useMapParam();
  const { scope } = useCurrentTeam();
  const stuffs = useStuffs(scope, map ?? "");
  if (!map) return <Navigate to="/library" replace />;
  if (stuffs.isPending) return <Loading />;
  if (stuffs.error) return <ErrorState message={errorText(t, stuffs.error)} onRetry={() => void stuffs.refetch()} />;

  const all = stuffs.data;
  const queue = learnQueue(all);
  const learned = all.filter((s) => s.myStatus === "learned").length;
  const tracked = queue.length + learned;
  const name = mapInfo(map).name;

  return (
    <div className="library">
      <MapBar map={map} view="learn" count={all.length} />
      <div className="page learn-page">
        <div className="page-head">
          <div>
            <p className="eyebrow">{t("learn.eyebrow")}</p>
            <h1 className="page-title">{t("learn.title", { map: name })}</h1>
            <p className="muted">{t("learn.intro")}</p>
          </div>
          {tracked > 0 && (
            <div className="learn-progress" aria-label={t("learn.progressLabel")}>
              <span className="learn-progress-number">
                {learned}/{tracked}
              </span>
              <span className="muted small">{t("learn.learnedOf")}</span>
              <span className="learn-meter" aria-hidden="true">
                <span style={{ width: `${Math.round((learned / tracked) * 100)}%` }} />
              </span>
            </div>
          )}
        </div>

        {queue.length > 0 ? (
          <ol className="learn-list">
            {queue.map((s, i) => (
              <LearnCard key={s.id} stuff={s} index={i} />
            ))}
          </ol>
        ) : (
          <div className="card learn-empty">
            <h2 className="section-title">{tracked > 0 ? t("learn.allDone", { map: name }) : t("learn.emptyTitle")}</h2>
            <p className="muted">{tracked > 0 ? t("learn.allDoneHelp") : t("learn.emptyHelp")}</p>
            <Link to={all.length ? `/library/${map}` : `/library/${map}/new`} className="btn btn-primary">
              {all.length ? t("learn.pickSome", { map: name }) : t("library.createFirst")}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
